import moment from 'moment';

const USER_FRIENDLY_FORMAT = 'h:mm a';
const LAST_FLIGHT = 'lastFlight';
const LAST_FLIGHT_FORMATS = ['lastflight', 'last', 'last flight', 'lf'];

export const MON = { label: 'MON', value: 'Mo' };
export const TUE = { label: 'TUE', value: 'Tu' };
export const WED = { label: 'WED', value: 'We' };
export const THU = { label: 'THU', value: 'Th' };
export const FRI = { label: 'FRI', value: 'Fr' };
export const SAT = { label: 'SAT', value: 'Sa' };
export const SUN = { label: 'SUN', value: 'Su' };

export const DAYS = [MON, TUE, WED, THU, FRI, SAT, SUN];

const continuousBucketToString = daysArray => {
    if (daysArray.length === 0) {
        return '';
    }

    if (daysArray.length === 1) {
        return daysArray[0].value;
    }

    return `${daysArray[0].value}-${daysArray[daysArray.length - 1].value}`;
};

const sort = daysArray => [...daysArray].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));

export const daysToBuckets = (daysArray = []) => {
    const buckets = [];
    sort(daysArray).forEach(day => {
        const dayIndex = DAYS.indexOf(day);
        // check if last item in buckets array has a last item that's the day before day
        const possibleBucket = buckets[buckets.length - 1];
        const possiblePreviousDay = possibleBucket?.[possibleBucket.length - 1];
        if (possiblePreviousDay && DAYS.indexOf(possiblePreviousDay) + 1 === dayIndex) {
            possibleBucket.push(day);
        } else {
            buckets.push([day]);
        }
    });
    return buckets;
};

export const daysToString = (daysArray = []) => {
    const buckets = daysToBuckets(daysArray);
    return buckets.map(continuousBucketToString).join(',');
};

const MOMENT_FORMATS = ['h:mm a', 'h:mma', 'ha', 'h a', 'H', 'H:mm'];
const IMPLICIT_AM_PM_FORMATS = ['h', 'h:mm'];

const isLastFlightString = (input, isOpens) => !isOpens && LAST_FLIGHT_FORMATS.includes(String(input).toLowerCase());

export const validateTimeString = (input, isOpens) => {
    let valid = false;

    if (!isOpens && isLastFlightString(input, isOpens)) {
        return true;
    }

    const isValidIncrement = () => {
        const convertedMoment = convertInputToMoment(input, isOpens);

        // Convert time to minutes and then check for 15 min increment
        return moment.duration(convertedMoment.format('HH:mm')).asMinutes() % 15 === 0;
    };

    for (const format of MOMENT_FORMATS) {
        if (moment(input, format, true).isValid() && isValidIncrement()) {
            valid = true;
            break;
        }
    }

    return valid;
};

export const convertInputToMoment = (input, isOpens) => {
    let shouldAppendPM = false;
    if (!isOpens) {
        for (const format of IMPLICIT_AM_PM_FORMATS) {
            if (moment(input, format, true).isValid()) {
                shouldAppendPM = true;
                break;
            }
        }
    }

    const modifiedInput = `${input}${shouldAppendPM ? 'pm' : ''}`;

    let result = null;
    for (const format of MOMENT_FORMATS) {
        const m = moment(modifiedInput, format, true);
        if (m.isValid()) {
            // const formatter = openingTime ? 'HH:mm' : 'kk:mm'
            // result = m.format(formatter)

            result = m;
            break;
        }
    }
    return result;
};

export const toUserFriendlyTimeString = (input, isOpens) => {
    if (isLastFlightString(input, isOpens)) {
        return LAST_FLIGHT;
    }

    const m = convertInputToMoment(input, isOpens);
    return m ? m.format(USER_FRIENDLY_FORMAT) : input;
};

export const userInputToSimplifiedOSMStrings = (daysArray, userFriendlyOpen, userFriendlyClose) => {
    const result = [];
    const buckets = daysToBuckets(daysArray);
    const closeTimeIsLastFlight = userFriendlyClose === LAST_FLIGHT;

    const startMoment = moment(userFriendlyOpen, USER_FRIENDLY_FORMAT);
    const endMoment = closeTimeIsLastFlight ? moment() : moment(userFriendlyClose, USER_FRIENDLY_FORMAT);

    const start = startMoment.isValid() ? startMoment.format('HH:mm') : 'invalid';
    let end = LAST_FLIGHT;
    if (!closeTimeIsLastFlight) {
        end = endMoment.isValid() ? endMoment.format('kk:mm') : 'invalid';
    }

    buckets.forEach(bucket => {
        const days = daysToString(bucket);
        result.push(`${days} ${start}-${end}`);
    });

    return result;
};

export const osmDaysToPrettyDays = daysString => {
    const replacements = DAYS.map(day => ({
        re: new RegExp(day.value, 'g'),
        pretty: day.label,
    }));

    replacements.push({ re: /,/g, pretty: ', ' });
    replacements.push({ re: /-/g, pretty: ' \u2013 ' });

    let result = daysString;
    replacements.forEach(replacementConfig => {
        result = result.replace(replacementConfig.re, replacementConfig.pretty);
    });

    return result;
};

export const osmHoursToPrettyHours = hoursString => {
    const hoursParts = hoursString.split('-');
    if (hoursParts.length !== 2) {
        return hoursString;
    }
    const prettyBegin = moment(hoursParts[0], 'HH:mm').format('h:mm a').toUpperCase();
    const prettyEnd =
        hoursParts[1] === LAST_FLIGHT ? 'Last Flight' : moment(hoursParts[1], 'kk:mm').format('h:mm a').toUpperCase();

    return `${prettyBegin} \u2013 ${prettyEnd}`;
};
